import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi, userApi } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(() => {
    try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
  });
  const [token, setToken]     = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(false);

  // Always refresh user profile from API when token exists.
  // This prevents stale role/campus data from localStorage after admin updates.
  useEffect(() => {
    if (!token) return;
    userApi.getMe()
      .then(r => {
        setUser(r.data.data);
        localStorage.setItem('user', JSON.stringify(r.data.data));
      })
      .catch(() => logout());
  }, [token]);

  function saveSession(data) {
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    if (data.deviceToken) localStorage.setItem('deviceToken', data.deviceToken);
    setToken(data.token);
    setUser(data.user);
  }

  async function sendOtp(email) {
    setLoading(true);
    try {
      const res = await authApi.sendOtp(email);
      return res.data;
    } finally { setLoading(false); }
  }

  async function requestAccount(fullName, email, campusID) {
    setLoading(true);
    try {
      const res = await authApi.requestAccount(fullName, email, campusID);
      return res.data;
    } finally { setLoading(false); }
  }

  async function microsoftLogin(idToken) {
    setLoading(true);
    try {
      const res = await authApi.microsoftLogin(idToken);
      if (res.data.success) saveSession(res.data.data);
      return res.data;
    } finally { setLoading(false); }
  }

  async function verifyOtp(email, otpCode, trustDevice, deviceName) {
    setLoading(true);
    try {
      const res = await authApi.verifyOtp(email, otpCode, trustDevice, deviceName);
      if (res.data.success) saveSession(res.data.data);
      return res.data;
    } finally { setLoading(false); }
  }

  async function trustedDeviceLogin(email) {
    const deviceToken = localStorage.getItem('deviceToken');
    if (!deviceToken) return { success: false, message: 'No trusted device found.' };
    setLoading(true);
    try {
      const res = await authApi.trustedDeviceLogin(email, deviceToken);
      if (res.data.success) saveSession(res.data.data);
      return res.data;
    } finally { setLoading(false); }
  }

  async function completeRegistration(campusID, fullName) {
    setLoading(true);
    try {
      const res = await authApi.completeRegistration(campusID, fullName);
      if (res.data.success) saveSession(res.data.data);
      return res.data;
    } finally { setLoading(false); }
  }

  async function logout() {
    try { await authApi.logout(); } catch {}
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  }

  function refreshUser(updatedUser) {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  }

  const isAdmin      = user?.roleName === 'Admin';
  const isManagement = user?.roleName === 'Management' || isAdmin;
  const isTeacher    = user?.roleName === 'Teacher';
  const isViewer     = user?.roleName === 'Viewer';

  return (
    <AuthContext.Provider value={{
      user, token, loading, isAdmin, isManagement, isTeacher, isViewer,
      sendOtp, requestAccount, microsoftLogin, verifyOtp, trustedDeviceLogin, completeRegistration, logout, refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
